import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import axios from 'axios';
import { debounce } from 'lodash';

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL;
const MAX_CACHE_SIZE = 50;
const CACHE_TTL = 30 * 60 * 1000;

export const useSearchStore = defineStore('search', () => {
  const searchTerm = ref('');
  const rawResults = ref([]);
  const suggestions = ref([]);
  const isLoading = ref(false);
  const error = ref(null);
  const itemsPerGroup = ref(5);
  const groupLimits = ref({});
  const searchCache = ref(new Map());

  // Bilingual search state
  const resultsLanguage = ref(null); // 'en' | 'fr' — the language the results came from
  const searchedInAlternateLang = ref(false); // true if results came from fallback language

  const currentStructure = ref(null);
  const structureBoss = ref(null);
  const structureBossContacts = ref(null);
  const structurePersonnels = ref([]);
  const structureContacts = ref(null);
  const structureMissions = ref([]);
  const structureRegionals = ref([]);

  const groupedResults = computed(() => {
    if (!rawResults.value || rawResults.value.length === 0) return [];

    const sortedResults = [...rawResults.value].sort((a, b) => b.match_score - a.match_score);
    const groupsMap = {};

    sortedResults.forEach(result => {
      const groupType = result.model_type;
      if (!groupsMap[groupType]) {
        groupsMap[groupType] = {
          type: getTypeLabel(groupType),
          items: [],
          limit: groupLimits.value[groupType] || itemsPerGroup.value
        };
      }
      groupsMap[groupType].items.push(result);
    });

    return Object.values(groupsMap);
  });

  const hasHighScoreRegionals = computed(() => {
    const regionalGroup = groupedResults.value.find(g => g.type === getTypeLabel('StructureRegionalDelegations'));
    if (!regionalGroup) return false;
    return regionalGroup.items.some(item => item.match_score >= 80);
  });

  function getTypeLabel(modelType) {
    const labels = {
      'Structure': 'Ministries & Organizations',
      'Boss': 'Officials',
      'StatePersonnels': 'Personnel',
      'StructureContacts': 'Organization Contacts',
      'StructureRegionalDelegations': 'Regional Delegations'
    };
    return labels[modelType] || modelType;
  }

  function getImageUrl(photoPath) {
    if (!photoPath) return null;
    if (photoPath.startsWith('http')) return photoPath;
    return `${API_BASE_URL}${photoPath.startsWith('/') ? '' : '/'}${photoPath}`;
  }

  function extractStructureData(result) {
    if (!result.related_data) return;

    const { structure, boss, boss_contacts, missions, regionals, personnels, contacts } = result.related_data;

    if (structure) {
      currentStructure.value = { ...structure, photo: getImageUrl(structure.photo || result.photo) };
    }

    if (boss) {
      structureBoss.value = { ...boss, photo: getImageUrl(boss.photo), boss_structure: structure };
    }

    structureBossContacts.value = boss_contacts || null;

    if (contacts) {
      structureContacts.value = { ...contacts };
    } else {
      structureContacts.value = null;
    }

    structureMissions.value = missions && Array.isArray(missions) ? missions : [];
    structureRegionals.value = regionals && Array.isArray(regionals) ? regionals.map(r => ({ ...r, photo: getImageUrl(r.photo) })) : [];
    structurePersonnels.value = personnels && Array.isArray(personnels) ? personnels.map(p => ({ ...p, photo: getImageUrl(p.photo) })) : [];
  }

  function _pruneCache() {
    if (searchCache.value.size <= MAX_CACHE_SIZE) return;
    const entries = [...searchCache.value.entries()];
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    const toRemove = entries.slice(0, entries.length - MAX_CACHE_SIZE);
    toRemove.forEach(([key]) => searchCache.value.delete(key));
  }

  function _isExpired(entry) {
    return Date.now() - entry.timestamp > CACHE_TTL;
  }

  /**
   * Core search — fetches results for a specific language.
   * Returns { results, suggestions } or null on error.
   */
  async function _fetchSearch(term, lang) {
    const headers = { 'Accept-Language': lang };
    try {
      const response = await axios.get(`${API_BASE_URL}/search/`, {
        params: {
          search_term: term,
          include_personnel: true,
          include_related: true,
          lang: lang
        },
        headers
      });

      if (!response.data) return { results: [], suggestions: [] };

      let results = [];
      let suggestionsList = [];

      if (response.data.results) {
        if (typeof response.data.results === 'object' && !Array.isArray(response.data.results)) {
          results = Object.values(response.data.results).flatMap(group => group.items || []);
        } else if (Array.isArray(response.data.results)) {
          results = response.data.results;
        }
      }

      suggestionsList = Array.isArray(response.data.suggestions) ? response.data.suggestions : [];
      return { results, suggestions: suggestionsList };
    } catch {
      return null;
    }
  }

  /**
   * Main search function with bilingual fallback.
   *
   * Strategy:
   * 1. Search in the current UI language
   * 2. If zero results, automatically retry in the OTHER language
   * 3. If the alternate language finds results, flag it so the UI can show a hint
   *
   * This handles the case where a user types "Ministère" while the UI is in English.
   */
  async function performSearch(lang = 'en') {
    if (!searchTerm.value.trim()) {
      clearSearch();
      return;
    }

    const term = searchTerm.value.trim();
    const altLang = lang === 'fr' ? 'en' : 'fr';

    // Check cache (includes language)
    const cacheKey = `search-${term}-${lang}`;
    if (searchCache.value.has(cacheKey)) {
      const cached = searchCache.value.get(cacheKey);
      if (!_isExpired(cached)) {
        rawResults.value = cached.results;
        suggestions.value = cached.suggestions;
        resultsLanguage.value = cached.resultsLang || lang;
        searchedInAlternateLang.value = cached.isAlternate || false;
        return;
      }
      searchCache.value.delete(cacheKey);
    }

    isLoading.value = true;
    error.value = null;
    searchedInAlternateLang.value = false;
    resultsLanguage.value = lang;

    try {
      // Fire BOTH language requests in parallel for bilingual coverage
      const [primary, alternate] = await Promise.all([
        _fetchSearch(term, lang),
        _fetchSearch(term, altLang),
      ]);

      const primaryCount = primary ? primary.results.length : 0;
      const altCount = alternate ? alternate.results.length : 0;
      const primaryTopScore = primaryCount > 0 ? (primary.results[0].match_score || 0) : 0;
      const altTopScore = altCount > 0 ? (alternate.results[0].match_score || 0) : 0;

      // Pick the best result set: prefer current language unless alt is clearly better
      const useAlternate = (
        (primaryCount === 0 && altCount > 0) ||
        (altTopScore > primaryTopScore + 15 && altCount > 0)
      );

      if (useAlternate) {
        rawResults.value = alternate.results;
        suggestions.value = alternate.suggestions;
        resultsLanguage.value = altLang;
        searchedInAlternateLang.value = true;

        searchCache.value.set(cacheKey, {
          results: alternate.results,
          suggestions: alternate.suggestions,
          resultsLang: altLang,
          isAlternate: true,
          timestamp: Date.now()
        });
      } else {
        rawResults.value = primary ? primary.results : [];
        suggestions.value = primary ? primary.suggestions : [];
        resultsLanguage.value = lang;
        searchedInAlternateLang.value = false;

        searchCache.value.set(cacheKey, {
          results: primary ? primary.results : [],
          suggestions: primary ? primary.suggestions : [],
          resultsLang: lang,
          isAlternate: false,
          timestamp: Date.now()
        });
      }
      _pruneCache();

    } catch {
      error.value = 'An error occurred during search. Please try again.';
      rawResults.value = [];
      suggestions.value = [];
    } finally {
      isLoading.value = false;
    }
  }

  const debouncedSearch = debounce(performSearch, 300);

  function clearSearch() {
    searchTerm.value = '';
    rawResults.value = [];
    suggestions.value = [];
    groupLimits.value = {};
    searchedInAlternateLang.value = false;
    resultsLanguage.value = null;
    clearStructureData();
  }

  function loadMore(groupType) {
    groupLimits.value[groupType] = (groupLimits.value[groupType] || itemsPerGroup.value) + 5;
  }

  async function fetchStructureData(structureId, lang = 'en') {
    isLoading.value = true;
    error.value = null;
    const headers = { 'Accept-Language': lang };

    try {
      const cachedResult = [...rawResults.value].find(
        result => result.model_type === 'Structure' &&
                 result.related_data?.structure?.id === structureId
      );

      if (cachedResult) {
        extractStructureData(cachedResult);
        return;
      }

      const [structureRes, bossRes, contactsRes, personnelsRes, missionsRes, regionalsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/structures/${structureId}/`, { headers }),
        axios.get(`${API_BASE_URL}/structure_retrieve_boss/?structureId=${structureId}`, { headers }),
        axios.get(`${API_BASE_URL}/structure_retrieve_contacts/?structureId=${structureId}`, { headers }),
        axios.get(`${API_BASE_URL}/state_personnels/?structureId=${structureId}`, { headers }),
        axios.get(`${API_BASE_URL}/structure_retrieve_missions/?structureId=${structureId}`, { headers }),
        axios.get(`${API_BASE_URL}/structure_retrieve_regionals/?structureId=${structureId}`, { headers })
      ]);

      currentStructure.value = { ...structureRes.data, photo: getImageUrl(structureRes.data.photo) };

      if (bossRes.data && bossRes.data.length > 0) {
        structureBoss.value = { ...bossRes.data[0], photo: getImageUrl(bossRes.data[0].photo), boss_structure: currentStructure.value };
      } else {
        structureBoss.value = null;
      }

      structureContacts.value = contactsRes.data?.[0] || null;
      structurePersonnels.value = personnelsRes.data?.map(p => ({ ...p, photo: getImageUrl(p.photo) })) || [];
      structureMissions.value = missionsRes.data || [];
      structureRegionals.value = regionalsRes.data?.map(r => ({ ...r, photo: getImageUrl(r.photo) })) || [];
    } catch {
      error.value = 'Failed to load structure details';
    } finally {
      isLoading.value = false;
    }
  }

  function clearStructureData() {
    currentStructure.value = null;
    structureBoss.value = null;
    structureBossContacts.value = null;
    structurePersonnels.value = [];
    structureContacts.value = null;
    structureMissions.value = [];
    structureRegionals.value = [];
  }

  function getResultRoute(result) {
    let path = '/';
    let query = {};

    switch(result.model_type) {
      case 'Structure':
        path = `/structure-details/${encodeURIComponent(result.name)}`;
        extractStructureData(result);
        break;
      case 'StructureRegionalDelegations':
        path = `/structure-details/${encodeURIComponent(result.parent_structure?.name || result.name)}`;
        if (result.parent_structure) query = { section: 'regional' };
        extractStructureData(result);
        break;
      case 'Boss':
        path = `/structure-details/${encodeURIComponent(result.boss_structure?.name || result.name)}`;
        if (result.boss_structure) query = { section: 'boss' };
        extractStructureData(result);
        break;
      case 'StatePersonnels':
      case 'StructurePersonnels':
        path = `/structure-details/${encodeURIComponent(result.structure?.name || result.name)}`;
        if (result.structure) query = { section: 'personnel' };
        extractStructureData(result);
        break;
      default:
        path = `/structure-details/${encodeURIComponent(result.name)}`;
    }

    return { path, query };
  }

  return {
    searchTerm,
    rawResults,
    groupedResults,
    suggestions,
    isLoading,
    error,
    itemsPerGroup,
    groupLimits,
    hasHighScoreRegionals,
    resultsLanguage,
    searchedInAlternateLang,
    currentStructure,
    structureBoss,
    structureBossContacts,
    structurePersonnels,
    structureContacts,
    structureMissions,
    structureRegionals,
    performSearch,
    debouncedSearch,
    clearSearch,
    loadMore,
    getImageUrl,
    getResultRoute,
    fetchStructureData,
    clearStructureData,
    extractStructureData
  };
});
